// C:\Users\phill\paperplay\frontend\src\features\accountsettings\account\slices\SubscriptionSlices.tsx

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import axiosInstance from '../../../../apis/common/axiosInstance';
import { ACCOUNT_SETTINGS } from '../../accountSettingsEndpoints';
import {
  SubscriptionData,
  SubscriptionState,
  Plan,
  PlanId,
  normalizeSubscription,
} from '../../subscriptions/subscriptionTypes';
import { RootState } from '../../../../store/rootReducer';

/* ----------------------------------------------------------------------------- */
/* Errors */
/* ----------------------------------------------------------------------------- */

const extractErrorMessage = (err: unknown, fallback: string) => {
  const error = err as { response?: { data?: unknown }; data?: unknown; message?: string };
  const data = error?.response?.data ?? error?.data ?? error;
  return (
    (typeof data === 'object' &&
      data !== null &&
      'detail' in data &&
      (data as { detail?: string }).detail) ||
    (typeof data === 'object' &&
      data !== null &&
      'error' in data &&
      (data as { error?: string }).error) ||
    (typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      (data as { message?: string }).message) ||
    error?.message ||
    fallback
  );
};

/* ----------------------------------------------------------------------------- */
/* Org context helpers (robust + local to subscriptions) */
/* ----------------------------------------------------------------------------- */

const ORG_HEADER = 'X-Account-Context';
const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

type WorkspaceApi = {
  id?: string; // membership id (backend)
  organization?: { id?: string }; // org id (backend)
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

function extractUuid(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const m = raw.match(UUID_RE);
  return m ? m[0] : null;
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizePlans(rawPlans: unknown[]): Plan[] {
  let previousMax: number | null = null;
  const output: Plan[] = [];

  for (let i = 0; i < rawPlans.length; i += 1) {
    const raw = rawPlans[i];
    if (!isRecord(raw)) continue;

    const idRaw =
      asString(raw.plan_id) ||
      asString(raw.planId) ||
      asString(raw.id) ||
      asString(raw.plan) ||
      asString(raw.name) ||
      asString(raw.nickname);
    const id = idRaw || `plan_${i + 1}`;

    const label =
      asString(raw.name) ||
      asString(raw.nickname) ||
      asString(raw.label) ||
      id;

    const priceRaw = asNumber(raw.monthly_amount_cents) ??
      asNumber(raw.unit_amount) ??
      asNumber(raw.priceCents) ??
      asNumber(raw.price_cents) ??
      asNumber(raw.price);
    const priceCents = priceRaw != null ? Math.max(0, Math.round(priceRaw)) : 0;

    const gigLimitRaw = asNumber(raw.gig_limit) ??
      asNumber(raw.gigLimit) ??
      asNumber(raw.maxShows) ??
      asNumber(raw.max_shows) ??
      asNumber(raw.limit);

    const isTopPlan =
      Boolean(raw.is_top_plan ?? raw.isTopPlan ?? raw.unlimited) ||
      (gigLimitRaw != null && gigLimitRaw >= 999999);

    const maxShows =
      isTopPlan ? null : gigLimitRaw != null ? Math.max(0, Math.floor(gigLimitRaw)) : null;

    const minShows = previousMax != null ? previousMax + 1 : 0;
    if (maxShows != null) previousMax = maxShows;

    const currency = (asString(raw.currency) || 'USD').toUpperCase();

    output.push({
      id: id as PlanId,
      label,
      minShows,
      maxShows,
      priceCents,
      currency,
    });
  }

  return output;
}

/**
 * Resolve an Organization UUID for subscription calls.
 * Priority:
 *  1) Redux connectedAccounts.activeId (should be org id after your slice fix)
 *  2) localStorage pp_active_connected_account / pp.accountContext (best-effort)
 *  3) /accountsettings/connected-accounts/ (authoritative; maps membership id -> org id if needed)
 *
 * If we cannot confidently resolve, return null.
 */
let _cachedOrgId: { id: string | null; at: number } = { id: null, at: 0 };
const ORG_CACHE_TTL_MS = 30_000;

function readCandidateFromState(state: RootState): string | null {
  const ca = (state as any)?.connectedAccounts;
  const activeId = ca?.activeId;
  const uuid = extractUuid(activeId);
  if (uuid) return uuid;
  return typeof activeId === 'string' && activeId.trim() ? activeId.trim() : null;
}

function readCandidateFromStorage(): string | null {
  try {
    const rawActive = localStorage.getItem('pp_active_connected_account');
    const u1 = extractUuid(rawActive) || rawActive;
    if (typeof u1 === 'string' && u1.trim()) return u1.trim();

    const rawCtx = localStorage.getItem('pp.accountContext');
    // If legacy context is "venue:..." / "artist:..." it's not an org id; ignore.
    if (
      rawCtx &&
      (rawCtx.toLowerCase().startsWith('venue:') || rawCtx.toLowerCase().startsWith('artist:'))
    ) {
      return null;
    }
    const u2 = extractUuid(rawCtx) || rawCtx;
    if (typeof u2 === 'string' && u2.trim()) return u2.trim();
  } catch {
    /* ignore */
  }
  return null;
}

function normalizeAccountsRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (isRecord(data) && Array.isArray((data as any).results)) return (data as any).results;
  return [];
}

/**
 * Query connected-accounts and return an org id.
 * - If candidate matches membership id, map it to organization.id
 * - If candidate matches organization.id, return it
 * - Else return first organization.id
 */
async function resolveOrgIdViaConnectedAccounts(
  signal: AbortSignal,
  candidate: string | null,
): Promise<string | null> {
  try {
    const { data } = await axiosInstance.get<unknown>(ACCOUNT_SETTINGS.CONNECTED_ACCOUNTS, { signal });
    const rows = normalizeAccountsRows(data);
    if (rows.length === 0) return null;

    const cand = (candidate || '').trim();

    if (cand) {
      for (const r of rows) {
        if (!isRecord(r)) continue;
        const w = r as WorkspaceApi;

        const orgId =
          extractUuid(w.organization?.id) ||
          (typeof w.organization?.id === 'string' ? w.organization.id.trim() : null);

        const membershipId =
          extractUuid(w.id) || (typeof w.id === 'string' ? w.id.trim() : null);

        if (orgId && cand === orgId) return orgId;
        if (orgId && membershipId && cand === membershipId) return orgId;
      }
    }

    for (const r of rows) {
      if (!isRecord(r)) continue;
      const w = r as WorkspaceApi;
      const orgId =
        extractUuid(w.organization?.id) ||
        (typeof w.organization?.id === 'string' ? w.organization.id.trim() : null);
      if (orgId && orgId.trim()) return orgId.trim();
    }

    return null;
  } catch {
    return null;
  }
}

async function resolveSubscriptionOrgId(getState: () => RootState, signal: AbortSignal): Promise<string | null> {
  const now = Date.now();
  if (_cachedOrgId.at && now - _cachedOrgId.at < ORG_CACHE_TTL_MS) {
    return _cachedOrgId.id;
  }

  const stateCandidate = readCandidateFromState(getState());
  const storageCandidate = readCandidateFromStorage();
  const candidate = stateCandidate || storageCandidate || null;

  const orgId = await resolveOrgIdViaConnectedAccounts(signal, candidate);

  _cachedOrgId = { id: orgId, at: now };
  return orgId;
}

/**
 * Always set the org header for subscription calls:
 * - If orgId is known -> set it
 * - Else -> set '' to neutralize any poisoned axios default header and allow membership fallback
 */
async function subscriptionRequestConfig(getState: () => RootState, signal: AbortSignal) {
  const orgId = await resolveSubscriptionOrgId(getState, signal);
  const safe = orgId && UUID_RE.test(orgId) ? orgId : '';
  return {
    signal,
    headers: {
      [ORG_HEADER]: safe,
    } as Record<string, string>,
  };
}

/* ----------------------------------------------------------------------------- */
/* Thunks */
/* ----------------------------------------------------------------------------- */

export const fetchPlans = createAsyncThunk<Plan[], void, { rejectValue: string }>(
  'subscription/fetchPlans',
  async (_, { rejectWithValue, signal }) => {
    try {
      const { data } = await axiosInstance.get<{ plans: unknown[] }>(
        ACCOUNT_SETTINGS.SUBSCRIPTION_PLANS,
        { signal },
      );
      const rawPlans = Array.isArray(data?.plans) ? data.plans : [];
      return normalizePlans(rawPlans);
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch plans.'));
    }
  },
);

export const startSubscription = createAsyncThunk<
  SubscriptionData & { requiresAction?: boolean; clientSecret?: string },
  { planId: string; paymentMethodId?: string; promotionCode?: string },
  { state: RootState; rejectValue: string }
>('subscription/start', async (payload, { rejectWithValue, getState, signal }) => {
  try {
    const cfg = await subscriptionRequestConfig(getState, signal);
    const { data } = await axiosInstance.post(
      ACCOUNT_SETTINGS.SUBSCRIPTION_CHANGE_PLAN,
      payload,
      cfg,
    );
    return data;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to start subscription.'));
  }
});

export const fetchSubscription = createAsyncThunk<
  SubscriptionData,
  void,
  { state: RootState; rejectValue: string }
>(
  'subscription/fetch',
  async (_, { rejectWithValue, getState, signal }) => {
    try {
      const cfg = await subscriptionRequestConfig(getState, signal);
      const { data } = await axiosInstance.get<SubscriptionData>(
        ACCOUNT_SETTINGS.SUBSCRIPTION_CURRENT,
        cfg,
      );
      return data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch subscription.'));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = selectSubscriptionState(getState() as RootState);
      return !state.loading;
    },
  },
);

export const changeSubscriptionPlan = createAsyncThunk<
  SubscriptionData & { requiresAction?: boolean; clientSecret?: string },
  string,
  { state: RootState; rejectValue: string }
>('subscription/changePlan', async (planId, { rejectWithValue, getState, signal }) => {
  try {
    const cfg = await subscriptionRequestConfig(getState, signal);
    const { data } = await axiosInstance.post(
      ACCOUNT_SETTINGS.SUBSCRIPTION_CHANGE_PLAN,
      { planId },
      cfg,
    );
    return data;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to change subscription plan.'));
  }
});

export const cancelSubscription = createAsyncThunk<
  SubscriptionData,
  { immediate?: boolean } | void,
  { state: RootState; rejectValue: string }
>('subscription/cancel', async (payload, { rejectWithValue, getState, signal }) => {
  try {
    const cfg = await subscriptionRequestConfig(getState, signal);
    const { data } = await axiosInstance.post<SubscriptionData>(
      ACCOUNT_SETTINGS.SUBSCRIPTION_CANCEL,
      payload ?? {},
      cfg,
    );
    return data;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to cancel subscription.'));
  }
});

export const applyPromoCode = createAsyncThunk<
  { ok: boolean; applied: boolean; code?: string },
  string,
  { rejectValue: string }
>('subscription/applyPromo', async (code, { rejectWithValue, signal }) => {
  try {
    const { data } = await axiosInstance.post(
      ACCOUNT_SETTINGS.SUBSCRIPTION_APPLY_PROMO,
      { code },
      { signal },
    );
    return data;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to apply promo code.'));
  }
});

/* ----------------------------------------------------------------------------- */
/* State */
/* ----------------------------------------------------------------------------- */

const initialState: SubscriptionState = {
  subscription: null,
  normalized: null,
  plans: [],
  loading: false,
  error: null,
  applyingPromo: false,
};

/* ----------------------------------------------------------------------------- */
/* Slice */
/* ----------------------------------------------------------------------------- */

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearSubscriptionError(state) {
      state.error = null;
    },
    mergeSubscription(state, action: PayloadAction<Partial<SubscriptionData>>) {
      state.subscription = {
        ...(state.subscription ?? ({} as SubscriptionData)),
        ...action.payload,
      };
      if (state.subscription) {
        state.normalized = normalizeSubscription(state.subscription, state.plans);
      }
    },
    resetSubscriptionState() {
      return initialState;
    },
  },
  extraReducers: builder => {
    const pending = (state: SubscriptionState) => {
      state.loading = true;
      state.error = null;
    };
    const rejected = (state: SubscriptionState, action: PayloadAction<string | undefined>) => {
      state.loading = false;
      state.error = action.payload ?? 'Unknown error';
    };

    builder
      .addCase(fetchPlans.pending, pending)
      .addCase(fetchPlans.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.plans = payload;
        if (state.subscription) {
          state.normalized = normalizeSubscription(state.subscription, state.plans);
        }
      })
      .addCase(fetchPlans.rejected, rejected)

      .addCase(fetchSubscription.pending, pending)
      .addCase(fetchSubscription.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.subscription = payload;
        state.normalized = normalizeSubscription(payload, state.plans);
      })
      .addCase(fetchSubscription.rejected, rejected)

      .addCase(changeSubscriptionPlan.pending, pending)
      .addCase(changeSubscriptionPlan.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.subscription = payload;
        state.normalized = normalizeSubscription(payload, state.plans);
      })
      .addCase(changeSubscriptionPlan.rejected, rejected)

      .addCase(cancelSubscription.pending, pending)
      .addCase(cancelSubscription.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.subscription = payload;
        state.normalized = normalizeSubscription(payload, state.plans);
      })
      .addCase(cancelSubscription.rejected, rejected)

      .addCase(applyPromoCode.pending, state => {
        state.applyingPromo = true;
        state.error = null;
      })
      .addCase(applyPromoCode.fulfilled, state => {
        state.applyingPromo = false;
      })
      .addCase(applyPromoCode.rejected, (state, action) => {
        state.applyingPromo = false;
        state.error = action.payload ?? 'Unknown error';
      })

      .addCase(startSubscription.pending, pending)
      .addCase(startSubscription.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.subscription = payload;
        state.normalized = normalizeSubscription(payload, state.plans);
      })
      .addCase(startSubscription.rejected, rejected);
  },
});

/* ----------------------------------------------------------------------------- */
/* Exports */
/* ----------------------------------------------------------------------------- */

export const { clearSubscriptionError, mergeSubscription, resetSubscriptionState } =
  subscriptionSlice.actions;

export default subscriptionSlice.reducer;

export const selectSubscriptionState = (state: RootState) => state.subscription;
